import type { QueueItem } from './approvals'

/**
 * The briefing a queue item hands to a Claude Code session.
 *
 * The fleet already knows the project, the check, the failing count, the
 * sample, the run and the links. Anyone retyping that into a session would
 * produce something worse and slower, so the item carries it.
 *
 * Built from the normalised item rather than from `context`, which means it
 * works for every shape the adapters read and for shapes that do not exist
 * yet: a new kind that yields assertions gets a briefing about its assertions
 * without anyone writing a branch here.
 *
 * Two rules, both about not wasting the session's first turn:
 *   - Only what the item actually carries. No placeholder headings, no "N/A".
 *   - Facts and samples verbatim. The point is that these were not retyped.
 */
export function buildPrompt(item: QueueItem): string {
  const lines: string[] = []

  lines.push(item.title)
  lines.push('')
  // Ahead of the summary, because the summary is the mechanics once the mapper
  // has taken the folded copy of this sentence out of it.
  if (item.plain) {
    lines.push(item.plain)
    lines.push('')
  }
  if (item.summary) {
    lines.push(item.summary)
    lines.push('')
  }

  const failing = item.assertions.filter((a) => a.ok === false)
  const unknown = item.assertions.filter((a) => a.ok === null)

  if (item.failure) {
    lines.push('## Why the run stopped')
    lines.push('')
    lines.push(item.failure.error)
    if (item.failure.attemptsUsed !== null) {
      const cap = item.failure.attemptCap !== null ? ` of ${item.failure.attemptCap}` : ''
      lines.push(`Gave up after attempt ${item.failure.attemptsUsed}${cap}.`)
    }
    if (item.failure.permanent === false) {
      lines.push('Not marked permanent, so a rerun is plausible once the cause is addressed.')
    }
    lines.push('')
  }

  if (failing.length > 0 || unknown.length > 0) {
    const noun = item.assertionNoun ?? 'checks'
    lines.push(`## Failing ${noun}`)
    lines.push('')
    for (const assertion of [...failing, ...unknown]) {
      const size = assertion.magnitude ? ` (${assertion.magnitude})` : ''
      const state = assertion.ok === null ? ' [status unknown]' : ''
      lines.push(`### ${assertion.label}${size}${state}`)
      if (assertion.note) lines.push(assertion.note)
      const samples = assertion.samplesFull.length > 0 ? assertion.samplesFull : assertion.samples
      if (samples.length > 0) {
        lines.push('')
        lines.push('Sample rows:')
        for (const sample of samples) lines.push(`- ${sample}`)
        if (assertion.sampleNote) lines.push(`(${assertion.sampleNote})`)
      }
      lines.push('')
    }
  }

  if (item.blocking.length > 0) {
    lines.push('## Blocking findings')
    lines.push('')
    for (const finding of item.blocking) {
      lines.push(`- ${finding.summary}`)
      if (finding.detail) lines.push(`  ${finding.detail}`)
      if (finding.cites) lines.push(`  cites: ${finding.cites}`)
    }
    lines.push('')
  }

  if (item.comments.length > 0) {
    lines.push('## Raised but not blocking')
    lines.push('')
    for (const comment of item.comments) {
      lines.push(`- ${comment.summary}`)
      if (comment.detail) lines.push(`  ${comment.detail}`)
    }
    lines.push('')
  }

  for (const group of item.proposals) {
    lines.push(`## ${group.heading}`)
    lines.push('')
    for (const entry of group.items) lines.push(`- ${entry}`)
    lines.push('')
  }

  if (item.evidence.length > 0) {
    lines.push('## Already verified, do not re-check')
    lines.push('')
    for (const entry of item.evidence) {
      lines.push(`- ${entry.claim}`)
      if (entry.found) lines.push(`  found: ${entry.found}`)
      if (entry.checked) lines.push(`  checked with: ${entry.checked}`)
    }
    lines.push('')
  }

  // Named fields and harvested scalars overlap -- `repo` is both -- so the
  // first mention of a label wins and the duplicate is dropped.
  const context = new Map<string, string>()
  const add = (label: string, value: string | null) => {
    if (value && !context.has(label.toLowerCase())) context.set(label.toLowerCase(), `- ${label}: ${value}`)
  }
  add('Repo', item.repo)
  add('Branch', item.buildBranch)
  for (const fact of item.facts) add(fact.label, fact.value)
  if (item.ticketRef) add('Ticket', `${item.ticketRef}${item.ticketUrl ? ` — ${item.ticketUrl}` : ''}`)
  if (item.refUrl && item.refUrl !== item.ticketUrl) add('Source', item.refUrl)
  add('Fleet run', item.runId)

  if (context.size > 0) {
    lines.push('## Context')
    lines.push('')
    lines.push(...context.values())
    lines.push('')
  }

  // Deliberately not an instruction to fix anything. What to do about a
  // finding is the operator's call, and a briefing that opens by assuming the
  // answer is how a session goes confidently in the wrong direction.
  lines.push('## What I need')
  lines.push('')
  lines.push(
    failing.length > 0 || item.failure
      ? 'Investigate the above and tell me what is actually wrong before changing anything. If a fix is obvious and small, propose it; if it is not, say what you would need to know.'
      : 'Read the above and tell me whether it needs anything doing. Say so plainly if it does not.',
  )

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n'
}
